import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Building2, Palette, CreditCard, Boxes, Users, MapPin, ShieldAlert, Bell, BatteryCharging, ShieldCheck, KeyRound, Sliders, Bot, Trash2, AudioLines, Smartphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAutoLocate } from "@/lib/map-prefs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTrackingProfile, type TrackingProfile } from "@/lib/tracking-prefs";

import { useAiHelperPrefs } from "@/lib/ai-helper-prefs";
import { useMrVoicePrefs } from "@/lib/voice-assistant/prefs";

export const Route = createFileRoute("/_authenticated/settings/")({
  component: SettingsPage,
  head: () => ({ meta: [{ title: "Settings — MissionReady" }] }),
});

type SosFlags = {
  sos_enabled: boolean;
  sos_hold_seconds: number;
  sos_double_tap_required: boolean;
  sos_emergency_services_enabled: boolean;
};

const MFA_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "org_owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "supervisor", label: "Supervisor" },
  { value: "site_supervisor", label: "Site Supervisor" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "auditor", label: "Auditor" },
  { value: "officer", label: "Officer" },
];

function SettingsPage() {
  const { currentOrg, currentRole } = useAuth();
  const [autoLocate, setAutoLocate] = useAutoLocate();
  const [trackingProfile, setTrackingProfileLocal] = useTrackingProfile();
  
  const [aiPrefs, setAiPrefs] = useAiHelperPrefs();
  const [voicePrefs, setVoicePrefs] = useMrVoicePrefs();
  const [sos, setSos] = useState<SosFlags | null>(null);
  const [savingSos, setSavingSos] = useState(false);
  const [mfaRoles, setMfaRoles] = useState<string[] | null>(null);
  const [savingMfa, setSavingMfa] = useState(false);
  const [requireNativeGps, setRequireNativeGps] = useState<boolean | null>(null);
  const [savingNativeGps, setSavingNativeGps] = useState(false);
  const isOwner = currentRole === "org_owner";
  const isAdmin = isOwner || currentRole === "admin";

  useEffect(() => {
    if (!currentOrg) return;
    supabase
      .from("org_feature_flags")
      .select("sos_enabled, sos_hold_seconds, sos_double_tap_required, sos_emergency_services_enabled, mfa_required_roles, require_native_live_gps")
      .eq("organization_id", currentOrg.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        const d = data as SosFlags & {
          mfa_required_roles: string[] | null;
          require_native_live_gps: boolean | null;
        };
        setRequireNativeGps(!!d.require_native_live_gps);
        setSos({
          sos_enabled: d.sos_enabled,
          sos_hold_seconds: d.sos_hold_seconds,
          sos_double_tap_required: d.sos_double_tap_required,
          sos_emergency_services_enabled: d.sos_emergency_services_enabled,
        });
        setMfaRoles(d.mfa_required_roles ?? []);
      });
  }, [currentOrg]);

  const toggleMfaRole = async (role: string, on: boolean) => {
    if (!currentOrg || mfaRoles === null) return;
    const next = on ? Array.from(new Set([...mfaRoles, role])) : mfaRoles.filter((r) => r !== role);
    setMfaRoles(next);
    setSavingMfa(true);
    const { error } = await supabase
      .from("org_feature_flags")
      .update({ mfa_required_roles: next })
      .eq("organization_id", currentOrg.id);
    setSavingMfa(false);
    if (error) toast.error(error.message);
    else toast.success("2FA policy updated");
  };

  // Live GPS device policy: when on, on-duty live location comes only from the
  // installed app; browsers show guards an install prompt instead.
  const saveRequireNativeGps = async (on: boolean) => {
    if (!currentOrg) return;
    setRequireNativeGps(on);
    setSavingNativeGps(true);
    const { error } = await supabase
      .from("org_feature_flags")
      .update({ require_native_live_gps: on } as never)
      .eq("organization_id", currentOrg.id);
    setSavingNativeGps(false);
    if (error) {
      setRequireNativeGps(!on);
      toast.error(error.message);
    } else {
      toast.success(on ? "Installed app required for live GPS" : "Browser live GPS allowed again");
    }
  };

  const saveSos = async (patch: Partial<SosFlags>) => {
    if (!currentOrg || !sos) return;
    const next = { ...sos, ...patch };
    setSos(next);
    setSavingSos(true);
    const { error } = await supabase
      .from("org_feature_flags")
      .update(patch)
      .eq("organization_id", currentOrg.id);
    setSavingSos(false);
    if (error) toast.error(error.message);
  };

  const items = [
    { to: "/settings/navigation" as const, icon: Sliders, label: "Navigation", desc: "Customize your bottom bar, sidebar order, and DAR placement." },
    { to: "/settings/security" as const, icon: ShieldCheck, label: "Security & 2FA", desc: "Manage your authenticators and session." },
    { to: "/settings/location-privacy" as const, icon: MapPin, label: "Location privacy", desc: "Read the GPS disclosure, see your acknowledgment, or withdraw it." },
    { to: "/settings/api-tokens" as const, icon: KeyRound, label: "API tokens", desc: "Issue org-scoped tokens for integrations." },
    { to: "/team" as const, icon: Users, label: "Team & invites", desc: "Manage members and roles." },
    { to: "/billing" as const, icon: CreditCard, label: "Billing & plan", desc: "Subscription and usage." },
    { to: "/modules" as const, icon: Boxes, label: "Modules", desc: "Enable optional features." },
    { to: "/settings/notifications" as const, icon: Bell, label: "Notifications", desc: "Push, in-app, and category preferences." },
    { to: "/settings/account" as const, icon: Trash2, label: "Account", desc: "Delete your account and data." },
  ];
  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <h1 className="font-display text-2xl font-bold md:text-3xl">Settings</h1>
      <p className="text-sm text-muted-foreground">{currentOrg?.name} · {currentRole}</p>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5">
          <Building2 className="h-4 w-4 text-primary" />
          <h3 className="mt-3 font-display text-lg font-semibold">Organization</h3>
          <dl className="mt-3 space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Name</dt><dd>{currentOrg?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Plan</dt><dd className="capitalize">{currentOrg?.plan === "free" ? "Free Forever" : currentOrg?.plan}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Slug</dt><dd className="font-mono text-xs">{currentOrg?.slug}</dd></div>
          </dl>
        </div>

        <Link
          to="/settings/branding"
          className="group rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary/50 hover:bg-card/80"
        >
          <Palette className="h-4 w-4 text-primary" />
          <h3 className="mt-3 font-display text-lg font-semibold">Branding</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Logo, primary &amp; accent colors, and custom subdomain.
          </p>
          <span className="mt-3 inline-block text-xs font-medium text-primary group-hover:underline">
            Open branding →
          </span>
        </Link>

        <div className="rounded-xl border border-border bg-card p-5">
          <MapPin className="h-4 w-4 text-primary" />
          <h3 className="mt-3 font-display text-lg font-semibold">Maps & location</h3>
          <p className="mt-2 text-sm text-muted-foreground">Control how maps use your device's location.</p>
          <div className="mt-4 flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
            <div className="min-w-0">
              <Label htmlFor="auto-locate" className="text-sm font-medium">Auto "locate me"</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                When on, maps request your location and center on you automatically.
              </p>
            </div>
            <Switch id="auto-locate" checked={autoLocate} onCheckedChange={setAutoLocate} />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <BatteryCharging className="h-4 w-4 text-primary" />
          <h3 className="mt-3 font-display text-lg font-semibold">GPS tracking profile</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Tune how often this device reports your location. Battery saver auto-engages on low battery.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            {([
              { id: "aggressive", label: "Aggressive", desc: "5s · active dispatch" },
              { id: "balanced", label: "Balanced", desc: "15s · default" },
              { id: "battery_saver", label: "Battery saver", desc: "30s · low-power" },
            ] as { id: TrackingProfile; label: string; desc: string }[]).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTrackingProfileLocal(opt.id)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  trackingProfile === opt.id
                    ? "border-primary bg-primary/10"
                    : "border-border bg-background/40 hover:border-primary/40"
                }`}
              >
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs text-muted-foreground">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>



        {requireNativeGps !== null && (
          <div className="rounded-xl border border-border bg-card p-5">
            <Smartphone className="h-4 w-4 text-primary" />
            <h3 className="mt-3 font-display text-lg font-semibold">Live GPS device policy</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Phone browsers stop sending positions the moment MissionReady isn't the screen a guard
              is looking at. Require the installed app so on-duty location keeps coming through with
              the screen locked.
            </p>
            <div className="mt-4 flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
              <div className="min-w-0">
                <Label htmlFor="require-native-gps" className="text-sm font-medium">
                  Require the app for live GPS
                </Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {isAdmin
                    ? "When on, guards on a browser see an install prompt instead of browser location sharing. Clock in/out, geofences and patrols are unchanged."
                    : "Owners and admins can change this."}
                </p>
              </div>
              <Switch
                id="require-native-gps"
                checked={requireNativeGps}
                disabled={!isAdmin || savingNativeGps}
                onCheckedChange={(v) => void saveRequireNativeGps(v)}
              />
            </div>
          </div>
        )}

        <div className="rounded-xl border border-border bg-card p-5">
          <Bot className="h-4 w-4 text-primary" />
          <h3 className="mt-3 font-display text-lg font-semibold">Floating AI Chatbot Helper</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Show the AI assistant as a movable floating helper instead of as a navigation page.
          </p>
          <div className="mt-4 flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
            <div className="min-w-0">
              <Label htmlFor="ai-orb" className="text-sm font-medium">Floating helper</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Drag it anywhere. Hold it over the bottom navigation bar for a few seconds to put the
                AI Assistant back in your menu. When off, use “AI Helper” in the menu.
              </p>
            </div>
            <Switch
              id="ai-orb"
              checked={aiPrefs.floating}
              onCheckedChange={(v) => setAiPrefs({ floating: v })}
            />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <AudioLines className="h-4 w-4 text-primary" />
          <h3 className="mt-3 font-display text-lg font-semibold">MissionReady Voice (“Hey MR”)</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Optional hands-free assistant. Ask about your next stop, who’s on site, the day’s
            schedule, incidents or geofence activity — and be taken straight into D.A.R., incident
            reporting or SOS. You only ever see what your role allows.
          </p>
          <div className="mt-4 flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
            <div className="min-w-0">
              <Label htmlFor="mr-voice" className="text-sm font-medium">Conversational mode</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Adds a voice button above the bottom navigation. Off by default.
              </p>
            </div>
            <Switch
              id="mr-voice"
              checked={voicePrefs.enabled}
              onCheckedChange={(v) => setVoicePrefs({ enabled: v })}
            />
          </div>
          {voicePrefs.enabled && (
            <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
              <div className="min-w-0">
                <Label htmlFor="mr-wake" className="text-sm font-medium">Listen for “Hey MR”</Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Works while the app is open and microphone access is allowed. Nothing is recorded
                  or kept while waiting for the phrase. Turn off to use the button only.
                </p>
              </div>
              <Switch
                id="mr-wake"
                checked={voicePrefs.wakePhrase}
                onCheckedChange={(v) => setVoicePrefs({ wakePhrase: v })}
              />
            </div>
          )}
        </div>

        {sos && (
          <div className="rounded-xl border border-destructive/40 bg-card p-5 sm:col-span-2 lg:col-span-3">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-destructive" />
              <h3 className="font-display text-lg font-semibold">SOS / Panic</h3>
              {!isOwner && <span className="ml-auto text-xs text-muted-foreground">Owner-only settings</span>}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure how the SOS button behaves and whether emergency-services escalation is available.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
                <div className="min-w-0">
                  <Label htmlFor="sos-enabled" className="text-sm font-medium">SOS enabled</Label>
                  <p className="mt-0.5 text-xs text-muted-foreground">Show the panic button across the app.</p>
                </div>
                <Switch id="sos-enabled" disabled={!isOwner || savingSos} checked={sos.sos_enabled}
                  onCheckedChange={(v) => saveSos({ sos_enabled: v })} />
              </div>
              <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
                <div className="min-w-0">
                  <Label htmlFor="sos-double" className="text-sm font-medium">Require double-tap</Label>
                  <p className="mt-0.5 text-xs text-muted-foreground">Prevents accidental triggers — user must tap twice before holding.</p>
                </div>
                <Switch id="sos-double" disabled={!isOwner || savingSos} checked={sos.sos_double_tap_required}
                  onCheckedChange={(v) => saveSos({ sos_double_tap_required: v })} />
              </div>
              <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
                <div className="min-w-0">
                  <Label htmlFor="sos-hold" className="text-sm font-medium">Hold seconds (staff SOS)</Label>
                  <p className="mt-0.5 text-xs text-muted-foreground">Default 3s. Emergency double-tap is always 5s.</p>
                </div>
                <Input id="sos-hold" type="number" min={1} max={10} className="w-20" disabled={!isOwner || savingSos}
                  value={sos.sos_hold_seconds}
                  onChange={(e) => setSos({ ...sos, sos_hold_seconds: Number(e.target.value) || 3 })}
                  onBlur={() => saveSos({ sos_hold_seconds: sos.sos_hold_seconds })} />
              </div>
              <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
                <div className="min-w-0">
                  <Label htmlFor="sos-em" className="text-sm font-medium">Emergency-services escalation</Label>
                  <p className="mt-0.5 text-xs text-muted-foreground">Shows a "Notify emergency services" toggle on every alert.</p>
                </div>
                <Switch id="sos-em" disabled={!isOwner || savingSos} checked={sos.sos_emergency_services_enabled}
                  onCheckedChange={(v) => saveSos({ sos_emergency_services_enabled: v })} />
              </div>
            </div>
          </div>
        )}

        {isOwner && mfaRoles !== null && (
          <div className="rounded-xl border border-border bg-card p-5 sm:col-span-2 lg:col-span-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h3 className="font-display text-lg font-semibold">2FA enforcement</h3>
              <span className="ml-auto text-xs text-muted-foreground">Owner-only</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Select roles that must enroll in two-factor authentication. Affected users will be redirected to Security &amp; 2FA until they enroll and verify.
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2 md:grid-cols-3">
              {MFA_ROLE_OPTIONS.map((opt) => {
                const on = mfaRoles.includes(opt.value);
                return (
                  <div key={opt.value} className="flex items-center justify-between rounded-lg border border-border bg-background/40 p-3">
                    <Label htmlFor={`mfa-${opt.value}`} className="text-sm font-medium">{opt.label}</Label>
                    <Switch
                      id={`mfa-${opt.value}`}
                      checked={on}
                      disabled={savingMfa}
                      onCheckedChange={(v) => toggleMfaRole(opt.value, v)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {items.map((i) => (
          <Link key={i.to} to={i.to} className="rounded-xl border border-border bg-card p-5 transition-all hover:border-primary/40">
            <i.icon className="h-4 w-4 text-primary" />
            <h3 className="mt-3 font-display text-lg font-semibold">{i.label}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{i.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
